import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';

interface KnowledgeBaseDeleteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => Promise<boolean | void>;
  count?: number;
  knowledgeBaseName?: string;
}

const KnowledgeBaseDeleteDialog = ({
  open,
  onOpenChange,
  onConfirm,
  count = 1,
  knowledgeBaseName,
}: KnowledgeBaseDeleteDialogProps) => {
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      const deleted = await onConfirm();
      if (deleted !== false) {
        onOpenChange(false);
      }
    } catch (error) {
      console.error('Error deleting knowledge base:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const isBulkDelete = count > 1;
  const itemLabel = knowledgeBaseName ? `“${knowledgeBaseName}”` : 'this knowledge base';
  const description = isBulkDelete
    ? `Are you sure you want to delete these ${count} knowledge bases? This action cannot be undone.`
    : `Are you sure you want to delete ${itemLabel}? This action cannot be undone.`;

  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        if (isDeleting) return;
        onOpenChange(value);
      }}
    >
      <DialogContent className="overflow-hidden border-border/70 bg-background/95 p-0 shadow-2xl sm:max-w-[460px]">
        <DialogHeader className="border-b border-border/60 px-6 py-5">
          <DialogTitle>{isBulkDelete ? 'Delete selected knowledge bases' : 'Delete knowledge base'}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="px-6 py-5 text-sm text-muted-foreground">
          {isBulkDelete
            ? 'All selected knowledge bases and their associated sources will be removed.'
            : 'This removes the knowledge base and its associated sources.'}
        </div>

        <DialogFooter className="border-t border-border/60 px-6 py-4">
          <Button
            type="button"
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleDelete}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Deleting...
              </>
            ) : isBulkDelete ? (
              'Delete selected'
            ) : (
              'Delete'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default KnowledgeBaseDeleteDialog;
